import { useState } from 'react';
import { api, formatBRL, type Platform, type Product } from '../api.js';

/**
 * Lote do WhatsApp — uma fila SEPARADA da fila de produção.
 *
 * O motivo de ser separada: a mesma oferta costuma ir para Instagram e TikTok
 * também, e o WhatsApp tem ritmo próprio (um post por hora, o dia inteiro). Se
 * o lote consumisse "as N primeiras da fila", escolher o que vai no WhatsApp e
 * o que vai nas outras redes seria impossível — era pegar tudo ou nada.
 *
 * Então a caixa é um destino de arraste: só o que está DENTRO dela sai por
 * WhatsApp; o resto continua na fila de cima, livre para as outras redes. Todo
 * arraste tem um botão equivalente ao lado — arrastar falha em tela de toque e
 * não existe para quem navega por teclado.
 *
 * Só WhatsApp por enquanto: é a única rede em que post sem mídia é publicável.
 * Instagram e TikTok exigem mídia por post — em lote, isso viraria uma pilha de
 * rascunhos que nunca sairiam, parecendo agendados.
 */

const INTERVALOS = [
  { min: 2, label: 'a cada 2 min' },
  { min: 3, label: 'a cada 3 min' },
  { min: 10, label: 'a cada 10 min' },
  { min: 15, label: 'a cada 15 min' },
  { min: 20, label: 'a cada 20 min' },
  { min: 30, label: 'a cada 30 min' },
  { min: 60, label: 'a cada 1 hora' },
  { min: 120, label: 'a cada 2 horas' },
] as const;

/** Próxima meia hora cheia: ninguém agenda para 14:23. */
function proximaMeiaHora(): string {
  const d = new Date(Date.now() + 5 * 60_000);
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Lê o id arrastado. Dois formatos porque nem todo navegador entrega o custom. */
function idArrastado(e: React.DragEvent): string | null {
  return (
    e.dataTransfer.getData('application/x-oferta') ||
    e.dataTransfer.getData('text/plain') ||
    null
  );
}

type Props = {
  /** As ofertas DENTRO da caixa, na ordem em que serão postadas. */
  items: Product[];
  /** Quantas sobraram na fila de cima — orienta quando a caixa está vazia. */
  disponiveis: number;
  onDropProduct: (productId: string) => void;
  onRemove: (productId: string) => void;
  onReorder: (productId: string, antesDe: string) => void;
  onScheduled: () => Promise<void> | void;
};

export function BatchPanel({
  items,
  disponiveis,
  onDropProduct,
  onRemove,
  onReorder,
  onScheduled,
}: Props) {
  const [intervalo, setIntervalo] = useState(60);
  const [inicio, setInicio] = useState(proximaMeiaHora);
  const [salvando, setSalvando] = useState(false);
  const [sobre, setSobre] = useState(false);
  const [aviso, setAviso] = useState<{ ok: boolean; texto: string } | null>(null);

  const plataforma: Platform = 'WHATSAPP';
  const inicioMs = new Date(inicio).getTime();
  const horaDo = (i: number) =>
    new Date(inicioMs + i * intervalo * 60_000).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  const fim = new Date(inicioMs + Math.max(0, items.length - 1) * intervalo * 60_000);

  async function agendar() {
    setSalvando(true);
    setAviso(null);
    try {
      const r = await api.scheduleBatch({
        items: items.map((p) => ({ productId: p.id })),
        platforms: [plataforma],
        startAt: new Date(inicio).toISOString(),
        intervalMinutes: intervalo,
        generateCaption: true,
      });
      setAviso({
        ok: true,
        texto:
          `${r.created} post(s) na Agenda, de ` +
          `${new Date(r.firstAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} até ` +
          `${new Date(r.lastAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}. ` +
          'Texto gerado por produto; o link entra sozinho na publicação.',
      });
      await onScheduled();
    } catch (cause) {
      setAviso({
        ok: false,
        texto: cause instanceof Error ? cause.message : 'Falha ao agendar o lote',
      });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div
      className={`lote-caixa${sobre ? ' sobre' : ''}`}
      onDragOver={(e) => {
        // Sem preventDefault o navegador recusa o soltar.
        e.preventDefault();
        setSobre(true);
      }}
      onDragLeave={() => setSobre(false)}
      onDrop={(e) => {
        e.preventDefault();
        setSobre(false);
        const id = idArrastado(e);
        if (id) onDropProduct(id);
      }}
    >
      <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
        <span aria-hidden="true">📲</span>
        <p className="section-title" style={{ margin: 0, flex: 1 }}>
          Lote do WhatsApp ({items.length})
        </p>
        {items.length > 0 && (
          <button
            type="button"
            className="ghost"
            style={{ fontSize: 12 }}
            onClick={() => items.forEach((p) => onRemove(p.id))}
          >
            esvaziar
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="muted" style={{ margin: '8px 0 0', fontSize: 12.5 }}>
          {disponiveis > 0 ? (
            <>
              Arraste para cá as ofertas da fila que vão sair por WhatsApp — ou clique no{' '}
              <strong>↓</strong> de cada uma. As que ficarem na fila continuam livres
              para Instagram e TikTok.
            </>
          ) : (
            'A fila de produção está vazia. Selecione ofertas na aba Ofertas primeiro.'
          )}
        </p>
      ) : (
        <>
          <ol className="lote-lista">
            {items.map((p, i) => (
              <li
                key={p.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/x-oferta', p.id);
                  e.dataTransfer.setData('text/plain', p.id);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  // Soltar SOBRE um item insere antes dele: é como se escolhe a
                  // ordem dos horários sem inventar um campo de número.
                  e.preventDefault();
                  e.stopPropagation();
                  const id = idArrastado(e);
                  if (id && id !== p.id) onReorder(id, p.id);
                }}
              >
                {/* Duas linhas, não uma.
                    A coluna da fila tem 250px: com hora, preço e botão na mesma
                    linha, sobravam ~55px para o nome e todos viravam
                    "Kit Kars…" — a lista deixava de responder qual produto é
                    qual, que é a única pergunta que ela existe para responder.
                    O nome agora tem a largura toda e até duas linhas. */}
                <div className="lote-topo">
                  <span className="lote-hora mono">{horaDo(i)}</span>
                  <span className="muted" style={{ fontSize: 11.5 }}>
                    {p.kind === 'PRODUCT' ? formatBRL(p.priceCents) : 'oferta'}
                  </span>
                  <button
                    type="button"
                    className="queue-move"
                    onClick={() => onRemove(p.id)}
                    title="Devolver para a fila"
                    aria-label={`Devolver ${p.name} para a fila`}
                  >
                    ↑
                  </button>
                </div>
                <span className="lote-nome" title={p.name}>
                  {p.name}
                </span>
              </li>
            ))}
          </ol>

          <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 150px' }}>
              <label htmlFor="lote-int">Ritmo</label>
              <select
                id="lote-int"
                value={intervalo}
                onChange={(e) => setIntervalo(Number(e.target.value))}
              >
                {INTERVALOS.map((i) => (
                  <option key={i.min} value={i.min}>
                    {i.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 170 }}>
              <label htmlFor="lote-ini">Começando</label>
              <input
                id="lote-ini"
                type="datetime-local"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
              />
            </div>
          </div>

          <p className="muted" style={{ margin: '10px 0 0', fontSize: 12.5 }}>
            {items.length} post(s) com texto da IA e link automático, até{' '}
            {fim.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            {fim.getDate() !== new Date(inicio).getDate() ? ' do dia seguinte' : ''}. As
            ofertas continuam na produção depois de agendadas.
          </p>

          {aviso && (
            <p
              className={aviso.ok ? 'muted' : 'err'}
              style={{
                margin: '8px 0 0',
                fontSize: 12.5,
                color: aviso.ok ? 'var(--gain)' : undefined,
              }}
            >
              {aviso.texto}
            </p>
          )}

          <button
            type="button"
            className="primary"
            style={{ marginTop: 10, maxWidth: 240 }}
            disabled={salvando}
            onClick={() => void agendar()}
          >
            {salvando ? <span className="spinner" /> : `Agendar ${items.length} post(s)`}
          </button>
        </>
      )}
    </div>
  );
}
